/**
 * How small and how large each sauna type can be drawn (issue 157).
 *
 * The one owner of the footprint limits: the dimension sliders read them, and
 * nothing else clamps a design's size. Outside the permit tiers or the span
 * table a design is not refused; the permit and roof-joist domains warn.
 *
 * `width` is the side the roof joists span (both rectangular roof systems
 * span across X), `length` the side they repeat along.
 */
import { getRuleValue } from "../../data/ruleRegistry";
import type { SaunaType } from "../../types/design";
import {
	PARTITION_RELAX_MIN_LENGTH_M,
	PARTITION_SAUNA_MIN_LENGTH_M,
} from "../partition/partitionSplit";
import { roofJoistTableSpanM } from "../snowzone/calculateRoofJoists";
import { STUD_D_M } from "./buildingDimensions";

export interface DimensionRange {
	min: number;
	max: number;
}

export interface DimensionLimits {
	width: DimensionRange;
	length: DimensionRange;
	/** m², the product of the two ranges' ends. */
	area: DimensionRange;
}

const floorToDecimetre = (m: number) => Math.floor(m * 10 + 1e-9) / 10;
const ceilToDecimetre = (m: number) => Math.ceil(m * 10 - 1e-9) / 10;

/**
 * Widest rectangular sauna: the longest clear span any conventional joist in
 * the span table carries (45×220 C24, c/c 600, snow zone 1,0: 3,63 × 1,19 =
 * 4,32 m) plus the two stud depths the joists bear on. In a higher zone the
 * same width is still drawn, and the roof-joist result says limträ is needed.
 */
const RECT_MAX_WIDTH_M = floorToDecimetre(
	(roofJoistTableSpanM(220, 1.0, 600) ?? 0) + 2 * STUD_D_M,
);

/** Swedish door module M7 (a 7×19 sauna door is 700 mm wide). */
const SMALLEST_DOOR_MM = 700;

/**
 * Smallest rectangular wall: a 7×19 door with the corner clearance
 * (`framing.minCornerClearanceMm`) on both sides, inside the two studs.
 */
const RECT_MIN_SIDE_M = ceilToDecimetre(
	(SMALLEST_DOOR_MM +
		2 * getRuleValue<number>("framing.minCornerClearanceMm")) /
		1000 +
		2 * STUD_D_M,
);

/**
 * Nothing structural binds the length: the joists span the width and simply
 * repeat. What binds it is the timber along it. The syll and hammarband run
 * the full length in one piece (the hammarband is the length less ~30 mm),
 * and the longest 45×95 C24 a Swedish merchant stocks is 5,4 m (Beijer:
 * 3,0–5,4 m). Past that the cut list would call for a board no one sells;
 * longer saunas need spliced plates, which the model does not draw yet.
 */
const LONGEST_STOCKED_45X95_M = 5.4;
const RECT_MAX_LENGTH_M = LONGEST_STOCKED_45X95_M;

/** A two-room design needs the smallest sauna room plus the smallest relax room. */
const TWO_ROOM_MIN_LENGTH_M = ceilToDecimetre(
	PARTITION_SAUNA_MIN_LENGTH_M + PARTITION_RELAX_MIN_LENGTH_M,
);

/**
 * Barrel: the limits it has always had, except the length. Its benches start
 * past the heater at the back and end mid-barrel (`L/2 − heater − 0,4 m`), so
 * under 2,0 m they had no length left and were drawn inside out.
 */
const BARREL_LIMITS = {
	width: { min: 1.5, max: 4.0 },
	length: { min: 2.0, max: 5.0 },
} as const;

function withArea(width: DimensionRange, length: DimensionRange) {
	return {
		width,
		length,
		area: {
			min: Number((width.min * length.min).toFixed(2)),
			max: Number((width.max * length.max).toFixed(2)),
		},
	};
}

export function getDimensionLimits(saunaType: SaunaType): DimensionLimits {
	if (saunaType === "tunna") {
		return withArea({ ...BARREL_LIMITS.width }, { ...BARREL_LIMITS.length });
	}
	return withArea(
		{ min: RECT_MIN_SIDE_M, max: RECT_MAX_WIDTH_M },
		{
			min: saunaType === "tvarum" ? TWO_ROOM_MIN_LENGTH_M : RECT_MIN_SIDE_M,
			max: RECT_MAX_LENGTH_M,
		},
	);
}
